import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CssBaseline, Box, CircularProgress, Typography, Button, Avatar } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { DialogProvider } from './contexts/DialogContext';
import { ScrollToTopOnMount, ScrollToTopButton } from './components/common/ScrollToTop';
import ErrorBoundary from './components/common/ErrorBoundary';
import { NotificationSchedulerService } from './services/notification-scheduler.service';
import { hasFeature, getRequiredTier } from './services/featureFlagService';
import { FeatureKeys, FeatureKey } from './models/featureKeys';
import { useDirection } from './hooks/useDirection';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import stylisRTLPlugin from 'stylis-plugin-rtl';
import './App.css';

// Lazy load pages for better performance
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const AuthActionPage = lazy(() => import('./pages/auth/AuthActionPage'));
const BlockedPage = lazy(() => import('./pages/auth/BlockedPage'));
const AccessDeniedPage = lazy(() => import('./pages/auth/AccessDeniedPage'));
const AdminUserManagementPage = lazy(() => import('./pages/admin/UserManagement'));
const AdminFeatureFlagsPage = lazy(() => import('./pages/admin/FeatureFlags'));
const AdminRevenuePage = lazy(() => import('./pages/admin/Revenue'));
const AdminFeedbackDashboardPage = lazy(() => import('./pages/admin/FeedbackDashboard'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const TripListPage = lazy(() => import('./pages/trips/TripListPage'));
const TripDetailPage = lazy(() => import('./pages/trips/TripDetailPage'));
const BookingInboxPage = lazy(() => import('./pages/bookings/BookingInboxPage'));
const AIPlannerPage = lazy(() => import('./pages/ai-planner/AIPlannerPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const DebugPage = lazy(() => import('./pages/debug/DebugPage'));
const SharedTripPage = lazy(() => import('./pages/shared/SharedTripPage'));
const RedeemPage = lazy(() => import('./pages/redeem/RedeemPage'));
const MarketPage = lazy(() => import('./pages/market/MarketPage'));
const MarketTripPage = lazy(() => import('./pages/market/MarketTripPage'));
const FamilyManagementPage = lazy(() => import('./pages/family/FamilyManagementPage'));
const LandingPage = lazy(() => import('./pages/landing/LandingPage'));
const SubscriptionPage = lazy(() => import('./pages/subscription/SubscriptionPage'));
const TermsPage = lazy(() => import('./pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'));
const RefundPage = lazy(() => import('./pages/legal/RefundPage'));

// Loading fallback component
const PageLoader: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      bgcolor: 'background.default',
    }}
  >
    <CircularProgress size={60} />
  </Box>
);

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Private Route Component — shows a loader while auth resolves, then redirects
// to /login if not signed in, or to /blocked if the admin has suspended the account.
export const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isBlocked, loading, profileLoading, userProfile } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  if (!currentUser) return <Navigate to="/login" />;
  // Everything below reads userProfile. Right after a sign-in `loading` is
  // already false while the profile snapshot is still in flight, so without
  // this wait a fully-registered user gets bounced to /profile (and an admin
  // to /trips) on every login.
  if (profileLoading) return <PageLoader />;
  if (isBlocked) return <Navigate to="/blocked" />;
  // Force profile completion in two cases:
  //  1. userProfile === null — the Auth account exists but users/{uid} does
  //     not. Mobile creates that document only when CompleteUserProfileScreen
  //     is submitted, so anyone who registered on mobile and never finished
  //     the form arrives here with no profile at all. Letting them through
  //     drops them into a tier-less app where every gated feature reads as
  //     locked and they are invisible to the admin Users list.
  //  2. A profile with no name (e.g. social sign-ins whose provider supplied
  //     no displayName) — they must set one before using the app, so they
  //     never render as blank rows in participant lists / share dialogs.
  // `loading` is already false here, so null means genuinely missing rather
  // than "the snapshot has not arrived yet".
  const needsProfileCompletion =
    !userProfile || !(userProfile.firstName?.trim() || userProfile.lastName?.trim());
  if (needsProfileCompletion && location.pathname !== '/profile') {
    // Carry the intended destination so ProfilePage can continue there once the
    // profile is saved, instead of stranding the user on the settings screen.
    return (
      <Navigate
        to="/profile"
        state={{ completeProfile: true, from: location.pathname + location.search }}
        replace
      />
    );
  }
  return <>{children}</>;
};

/**
 * AdminRoute — accessible only to users with isAdmin = true.
 */
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isBlocked, isAdmin, loading, profileLoading } = useAuth();
  if (loading) return <PageLoader />;
  if (!currentUser) return <Navigate to="/login" />;
  if (profileLoading) return <PageLoader />;
  if (isBlocked) return <Navigate to="/blocked" />;
  if (!isAdmin) return <Navigate to="/trips" />;
  return <>{children}</>;
};

/**
 * Like PrivateRoute but also checks the user's subscription tier.
 * Shows an inline locked-feature screen if the user can't access [featureKey].
 */
const FeatureGatedRoute: React.FC<{ children: React.ReactNode; featureKey: FeatureKey }> = ({
  children,
  featureKey,
}) => {
  const { currentUser, isBlocked, loading, profileLoading, userProfile } = useAuth();
  if (loading) return <PageLoader />;
  if (!currentUser) return <Navigate to="/login" />;
  // Wait for the profile — hasFeature() on a null profile reads as Free, which
  // would flash the locked-feature screen at a paying user on every login.
  if (profileLoading) return <PageLoader />;
  if (isBlocked) return <Navigate to="/blocked" />;

  if (!hasFeature(userProfile, featureKey)) {
    const required = getRequiredTier(featureKey);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: 'background.default', p: 3 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
          <Avatar sx={{ bgcolor: 'warning.main', width: 80, height: 80, mx: 'auto', mb: 2.5 }}>
            <LockOutlinedIcon sx={{ fontSize: 44, color: 'white' }} />
          </Avatar>
          <Typography variant="h5" fontWeight="bold" gutterBottom color="text.primary">Pro Feature</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            This feature requires the <strong>{required}</strong> tier.
            Contact your administrator to upgrade your account.
          </Typography>
          <Button variant="outlined" onClick={() => window.history.back()}>Go Back</Button>
        </Box>
      </Box>
    );
  }

  return <>{children}</>;
};

/**
 * Watches auth state and starts/stops the notification + email schedulers.
 * Must live inside <AuthProvider> so useAuth() works.
 */
const AuthSchedulerWatcher: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  useEffect(() => {
    if (currentUser && userProfile) {
      const effectiveEmail = userProfile.realEmail || userProfile.email;
      NotificationSchedulerService.start(currentUser.uid, effectiveEmail);
    } else {
      NotificationSchedulerService.stop();
    }
    return () => NotificationSchedulerService.stop();
  }, [currentUser?.uid, userProfile]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

const ltrCache = createCache({ key: 'muiltr' });
const rtlCache = createCache({ key: 'muirtl', stylisPlugins: [stylisRTLPlugin as any] });

/**
 * Inner app wrapper that reads RTL state and applies appropriate Emotion cache.
 */
const DirectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isRTL } = useDirection();
  return (
    <CacheProvider value={isRTL ? rtlCache : ltrCache}>
      {children}
    </CacheProvider>
  );
};

function App() {
  useEffect(() => {
    // Suppress known Firestore transient errors that don't affect functionality
    const handleError = (event: ErrorEvent) => {
      if (event.message?.includes('Unexpected state') && event.message?.includes('ca9')) {
        event.preventDefault();
        return false;
      }
      return true;
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  return (
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <DirectionProvider>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <CssBaseline />
                <DialogProvider>
                  <AuthProvider>
                    <AuthSchedulerWatcher />
                    <Router>
                      <ScrollToTopOnMount />
                      <ScrollToTopButton />
                      <Suspense fallback={<PageLoader />}>
                        <Routes>
                          <Route path="/login" element={<LoginPage />} />
                          <Route path="/auth/action" element={<AuthActionPage />} />
                          {/* /blocked and /access-denied are public — not wrapped in PrivateRoute to avoid redirect loops */}
                          <Route path="/blocked" element={<BlockedPage />} />
                          <Route path="/access-denied" element={<AccessDeniedPage />} />
                          <Route path="/shared/:token" element={<SharedTripPage />} />
                          {/* Public clone-share bridge — guides visitors to install the app and use the code */}
                          <Route path="/redeem/:code" element={<RedeemPage />} />
                          {/* Trip Market — public catalog. Deliberately outside
                              PrivateRoute: logged-out visitors must be able to
                              browse and open a published trip, then sign in only
                              when they want to save a copy. */}
                          <Route path="/market" element={<MarketPage />} />
                          <Route path="/market/:tripId" element={<MarketTripPage />} />
                          <Route
                            path="/debug"
                            element={
                              <PrivateRoute>
                                <DebugPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/trips"
                            element={
                              <PrivateRoute>
                                <TripListPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/trips/:tripId"
                            element={
                              <PrivateRoute>
                                <TripDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/bookings"
                            element={
                              <FeatureGatedRoute featureKey={FeatureKeys.emailBookingsInbox}>
                                <BookingInboxPage />
                              </FeatureGatedRoute>
                            }
                          />
                          <Route
                            path="/ai-planner"
                            element={
                              <FeatureGatedRoute featureKey={FeatureKeys.createTripAi}>
                                <AIPlannerPage />
                              </FeatureGatedRoute>
                            }
                          />
                          <Route
                            path="/profile"
                            element={
                              <PrivateRoute>
                                <ProfilePage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/family"
                            element={
                              <PrivateRoute>
                                <FamilyManagementPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/subscription"
                            element={
                              <PrivateRoute>
                                <SubscriptionPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/admin"
                            element={
                              <AdminRoute>
                                <AdminDashboard />
                              </AdminRoute>
                            }
                          />
                          {/* Legacy admin routes — redirect to unified AdminDashboard */}
                          <Route path="/admin/users" element={<Navigate to="/admin?tab=users" replace />} />
                          <Route path="/admin/feature-flags" element={<Navigate to="/admin?tab=flags" replace />} />
                          <Route path="/admin/revenue" element={<Navigate to="/admin?tab=revenue" replace />} />
                          <Route path="/admin/feedback" element={<Navigate to="/admin?tab=feedback" replace />} />
                          {/* Public legal pages — no auth required */}
                          <Route path="/terms" element={<TermsPage />} />
                          <Route path="/privacy" element={<PrivacyPage />} />
                          <Route path="/refund" element={<RefundPage />} />
                          <Route path="/" element={<LandingPage />} />
                        </Routes>
                      </Suspense>
                    </Router>
                  </AuthProvider>
                </DialogProvider>
              </LocalizationProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </DirectionProvider>
      </I18nextProvider>
    </ErrorBoundary>
  );
}

export default App;
